import { createContext, useContext, useState, useEffect } from "react";
import notificationService from "../services/notificationService";
import pushNotificationService from "../services/pushNotificationService";
import apiClient from "../ApiClient";
import { is } from "date-fns/locale";

const WebNotificationContext = createContext();

export const WebNotificationProvider = ({ userId, children }) => {
  const { fetchUserNotifications } = notificationService();
  const { request } = apiClient();
  const { isSupported, initialize, registration, registerServiceWorker, requestPermission, subscribeToPushNotifications, unsubscribe } = pushNotificationService(request);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(() => {
    const stored = sessionStorage.getItem(`pushEnabled_${userId}`);
    return stored ? JSON.parse(stored) : false;
  });
  const [pushSupported, setPushSupported] = useState(false);

  const refreshUnreadCount = async () => {
    if (!userId) return;

    try {
      const data = await fetchUserNotifications(userId);
      const unread = data.content?.filter((n) => !n.isRead).length || 0;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Failed to refresh unread count:', error);
    }
  };

  const checkExistingSubscription = async () => {
    console.log('Mobile Debug: Checking existing subscription for userId:', userId);
    console.log('Mobile Debug: Browser info:', {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      serviceWorkerSupported: 'serviceWorker' in navigator,
      pushManagerSupported: 'PushManager' in window,
      notificationSupported: 'Notification' in window,
      notificationPermission: Notification.permission
    });

    if (!userId) return false;

    try {
      const response = await request('POST', '/notifications/subscription-status', { userId });
      console.log('Mobile Debug: Subscription status from backend:', response);
      console.log('Mobile Debug: Response data:', response.data);
      console.log('Mobile Debug: Is subscribed:', response.data?.isSubscribed);
      return response.data?.isSubscribed || false;
    } catch (error) {
      console.error('Mobile Debug: Backend subscription check failed:', error);
      try {
        const { registration } = pushNotificationService();
        console.log('Mobile Debug: Checking browser subscription directly...');
        if (registration) {
          const subscription = await registration.pushManager.getSubscription();
          console.log('Mobile Debug: Browser subscription:', subscription);
          return subscription !== null;
        }
      } catch (browserError) {
        console.error('Failed to check browser subscription:', browserError);
      }
      return false;
    }
  };

  const initializePushNotifications = async () => {
    if (!userId) return;

    try {
      console.log('Mobile Debug: Is push notifications supported:', isSupported());
      setPushSupported(isSupported());

      if (isSupported()) {

        const storedStatus = sessionStorage.getItem(`pushEnabled_${userId}`);
        const isFirstLogin = sessionStorage.getItem(`user_${userId}_first_login`) === 'true';
        console.log('Mobile Debug: Stored pushEnabled status:', storedStatus, 'Is first login:', isFirstLogin);
        const hasExistingSubscription = await checkExistingSubscription();
        console.log('Mobile Debug: Has existing subscription:', hasExistingSubscription);
        
        if (hasExistingSubscription || (storedStatus === 'true' && Notification.permission === 'granted')) {
          console.log('Mobile Debug: Using existing subscription');
          setPushEnabled(true);
          sessionStorage.setItem(`pushEnabled_${userId}`, 'true');
        } else {
          console.log('Mobile Debug: No existing subscription, checking permission...');
          const permission = Notification.permission;
          console.log('Mobile Debug: Current notification permission:', permission);
          
          if (permission === 'granted' || isFirstLogin) {
            console.log('Mobile Debug: Permission granted OR first login - calling enablePushNotifications');
            await enablePushNotifications();
            if (isFirstLogin) {
              console.log('Mobile Debug: Clearing first login flag');
              sessionStorage.removeItem(`user_${userId}_first_login`);
            }
          } else if (permission === 'default') {
            console.log('Mobile Debug: Permission default - calling enablePushNotifications');
            await enablePushNotifications();
          } else {
            console.log('Mobile Debug: Permission denied - setting pushEnabled to false');
            setPushEnabled(false);
            sessionStorage.setItem(`pushEnabled_${userId}`, 'false');
          }
        }
      }
    } catch (error) {
      console.error('Failed to initialize push notifications:', error);
      setPushEnabled(false);
      sessionStorage.setItem(`pushEnabled_${userId}`, 'false');
    }
  };

  const enablePushNotifications = async () => {
    console.log('Mobile Debug: Starting enablePushNotifications');
    console.log('Mobile Debug: Current permission:', Notification.permission);
    try {
      if (!registration) {
        console.log('Mobile Debug: Registering service worker...');
        await registerServiceWorker();
      }
      console.log('Mobile Debug: Requesting permission...');

      const permission = await requestPermission();
      console.log('Mobile Debug: Permission result:', permission);

      if (permission === 'granted') {
        console.log('Mobile Debug: Permission granted, subscribing...');

        await subscribeToPushNotifications(userId);
        console.log('Mobile Debug: Subscription successful');

        setPushEnabled(true);
        sessionStorage.setItem(`pushEnabled_${userId}`, 'true');
      }
    } catch (error) {
      console.error('Failed to enable push notifications:', error);
      setPushEnabled(false);
      sessionStorage.setItem(`pushEnabled_${userId}`, 'false');
    }
  };

  const disablePushNotifications = async () => {
    // Disabled: Users cannot manually disable push notifications
    // This functionality is reserved for future admin panel
    console.log('Push notifications cannot be disabled by users');
  };

  useEffect(() => {
    if (userId) {
      refreshUnreadCount();
      initializePushNotifications();
    }
  }, [userId]);

  // Effect to update pushEnabled state when userId changes
  useEffect(() => {
    if (userId) {
      const stored = sessionStorage.getItem(`pushEnabled_${userId}`);
      if (stored) {
        setPushEnabled(JSON.parse(stored));
      }
    }
  }, [userId]);

  return (
    <WebNotificationContext.Provider value={{
      unreadCount,
      setUnreadCount,
      refreshUnreadCount,
      pushEnabled,
      pushSupported,
      enablePushNotifications
      // disablePushNotifications removed - reserved for future admin panel
    }}>
      {children}
    </WebNotificationContext.Provider>
  );
};

export const useWebNotificationContext = () => useContext(WebNotificationContext);